import React, { useState } from 'react';
import { supabase } from '../../config/supabase';
import { useAwningPricing } from '../../hooks/useAwningPricing';

const AwningsHeroSection: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  // Get pricing from database
  const { pricingByModel, isLoading } = useAwningPricing();

  // Calculate the lowest price across all models
  const getLowestPrice = (): string => {
    if (isLoading || !pricingByModel) {
      return '$1,794'; // Fallback to expected lowest price
    }

    const allPricing = Object.values(pricingByModel).flat();
    if (allPricing.length === 0) {
      return '$1,794'; // Fallback if no pricing data
    }

    const prices = allPricing
      .filter(p => p.total_price > 0)
      .map(p => p.total_price);

    if (prices.length === 0) {
      return '$1,794'; // Fallback if no valid prices
    }

    const lowestPrice = Math.min(...prices);
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(lowestPrice);
  };

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phone.trim()) {
      setShowError(true);
      return;
    }

    setIsSubmitting(true);
    setShowError(false);

    try {
      const cleanPhone = phone.replace(/\D/g, '');
      
      const insertData = {
        phone: cleanPhone,
        best_time: bestTime || 'anytime',
        source: 'awnings_hero_section'
      };

      const { error } = await supabase
        .from('quick_callbacks')
        .insert([insertData]);

      if (error) {
        console.error('Supabase error:', error);
        setShowError(true);
      } else {
        setShowSuccess(true);
        setPhone('');
        setBestTime('');
        
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting callback request:', error);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="gradient-cool py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center text-white">
          {/* Sale Badge */}
          <div className="inline-block bg-white text-cool px-6 py-3 rounded-full mb-6 pulse-sale">
            <span className="font-bold">BEAT THE SUMMER HEAT</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Motorized Retractable Awnings
          </h1>
          <p className="text-xl md:text-2xl mb-2">Cool Your Patio by Up to 12°C</p>
          <p className="text-lg mb-8 max-w-3xl mx-auto opacity-90">
            Push-button shade control with premium German motors and weather-resistant fabrics.
            Professional installation across Ontario with smart home integration.
          </p>

          {/* Energy Savings Highlight */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-2">12°C</div>
              <p className="text-sm opacity-90">Cooler Patio</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-2">$200-400</div>
              <p className="text-sm opacity-90">Annual AC Savings</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-2">From {getLowestPrice()}</div>
              <p className="text-sm opacity-90">Installed</p>
            </div>
          </div>

          {/* Quick Callback Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-8 max-w-md mx-auto mb-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-primary mb-4">Get Cooling Consultation</h3>
            <p className="text-gray-600 mb-6">Speak with our awning specialists within 24 hours</p>
            
            <form onSubmit={handleCallbackSubmit} className="space-y-4">
              <div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Your phone number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <select
                  value={bestTime}
                  onChange={(e) => setBestTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="">Best time to call (optional)</option>
                  <option value="morning">Morning (9 AM - 12 PM)</option>
                  <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                  <option value="evening">Evening (5 PM - 8 PM)</option>
                  <option value="anytime">Anytime</option>
                </select>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : '📞 Get Cooling Consultation'}
              </button>
            </form>

            {showSuccess && (
              <div className="mt-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <p>✅ Thank you! Our awning expert will call you within 24 hours.</p>
              </div>
            )}

            {showError && (
              <div className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <p>❌ Something went wrong. Please try again.</p>
              </div>
            )}
          </div>

          {/* Additional CTA Options */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href="/awning-quote"
              className="bg-white text-primary px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block shadow-lg"
            >
              ⚡ Get Instant Quote (Under 1 Min)
            </a>
            <a
              href="#awnings"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary transition-colors inline-block"
            >
              🌡️ See Models & Pricing
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">⭐</span>
              <span className="font-semibold">German Somfy Motors • Smart Home Ready • 47+ Happy Customers</span>
            </div>
            <div className="flex items-center gap-1 text-yellow-400">
              <span>★★★★★</span>
              <span className="text-white ml-2">4.9/5 Customer Rating</span>
            </div>
          </div>

          {/* Energy Savings Note */}
          <div className="text-center mt-8">
            <p className="text-lg opacity-90">
              <strong>Average savings:</strong> $200-400 per summer on energy costs
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AwningsHeroSection;
